import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CalendarClock, FileSignature, AlertCircle, ShieldAlert, Scale, BellRing, BellOff, Send } from "lucide-react";
import LetterModal from "@/components/LetterModal";
import { pushSupported, getPushStatus, enablePush, disablePush, sendTestPush } from "@/lib/push";

function fmt(dateStr) {
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return dateStr;
  }
}

function urgency(days) {
  if (days < 0) return { label: "Échéance dépassée", cls: "text-destructive", bg: "border-destructive/40 bg-destructive/5", act: true };
  if (days <= 30) return { label: `Dans ${days} j`, cls: "text-destructive", bg: "border-destructive/40 bg-destructive/5", act: true };
  if (days <= 60) return { label: `Dans ${days} j`, cls: "text-ochre", bg: "border-ochre/40 bg-ochre/5", act: true };
  return { label: `Dans ${days} j`, cls: "text-emerald-600", bg: "border-border bg-card", act: false };
}

function chatelNote(days) {
  if (days < 0) return "Échéance dépassée : vérifiez si le contrat a été reconduit tacitement. Loi Chatel : si l'assureur vous a prévenu tard, vous avez 20 jours pour résilier.";
  if (days <= 60) return "Fenêtre de résiliation : anticipez le préavis (souvent 2 mois avant l'échéance). Loi Hamon : après 1 an d'engagement, vous pouvez résilier à tout moment.";
  return "Loi Hamon : résiliable à tout moment après 1 an d'engagement. Loi Chatel : l'assureur doit vous rappeler la date limite de résiliation.";
}

function PushCard() {
  const [status, setStatus] = useState({ enabled: false, configured: false });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const supported = pushSupported();

  useEffect(() => {
    getPushStatus().then(setStatus);
  }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (status.enabled) {
        await disablePush();
        setStatus((s) => ({ ...s, enabled: false, count: 0 }));
        toast.success("Rappels désactivés");
      } else {
        await enablePush();
        setStatus((s) => ({ ...s, enabled: true }));
        toast.success("Rappels activés — vous serez prévenu avant chaque échéance");
      }
    } catch (e) {
      toast.error(e.message || "Impossible de modifier les rappels");
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      await sendTestPush();
      toast.success("Notification de test envoyée 📩");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message || "Échec du test");
    } finally {
      setTesting(false);
    }
  };

  if (!status.configured) return null;

  return (
    <div data-testid="push-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`w-11 h-11 rounded-xl grid place-items-center shrink-0 ${status.enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
          {status.enabled ? <BellRing className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="font-heading font-bold">Rappels de renouvellement par notification</p>
          <p className="text-sm text-muted-foreground mt-0.5">
            {status.enabled
              ? "Activés — vous recevrez une alerte 30, 7 et 1 jour avant chaque échéance, même l'app fermée."
              : "Recevez une notification 30, 7 et 1 jour avant chaque échéance. Ne ratez plus jamais une résiliation."}
          </p>
          {!supported && (
            <p className="text-xs text-ochre mt-2">Sur iPhone, installez d'abord l'app sur l'écran d'accueil pour recevoir les notifications.</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {status.enabled && (
            <button
              data-testid="push-test-btn"
              onClick={test}
              disabled={testing}
              className="flex items-center gap-1.5 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition disabled:opacity-60"
            >
              {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Tester
            </button>
          )}
          <button
            data-testid="push-toggle-btn"
            onClick={toggle}
            disabled={busy || !supported}
            className={`flex items-center gap-1.5 h-10 px-4 rounded-xl text-sm font-medium transition disabled:opacity-60 ${status.enabled ? "border border-border hover:bg-muted" : "bg-primary text-primary-foreground hover:opacity-90"}`}
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : status.enabled ? <BellOff className="w-4 h-4" /> : <BellRing className="w-4 h-4" />}
            {status.enabled ? "Désactiver" : "Activer les rappels"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RemindersView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [letter, setLetter] = useState({ open: false, loading: false, name: "", text: "" });

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/reminders");
        setItems(data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const genLetter = async (id, name) => {
    setLetter({ open: true, loading: true, name, text: "" });
    try {
      const { data } = await api.post("/cancellation/letter", { contract_id: id });
      setLetter({ open: true, loading: false, name: data.contract_name, text: data.letter });
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Erreur");
      setLetter({ open: false, loading: false, name: "", text: "" });
    }
  };

  const actionable = items.filter((it) => typeof it.days_left === "number" && it.days_left <= 60).length;

  return (
    <div className="space-y-8 animate-in-up" data-testid="reminders-view">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.18em] text-muted-foreground">Échéances</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight mt-1">Renouvellements & résiliations</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Anticipez la fin de vos contrats. Générez la lettre de résiliation recommandée en un clic — plus d'excuse pour la flemme.</p>
      </div>

      <PushCard />

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground text-sm flex flex-col items-center gap-3">
          <AlertCircle className="w-8 h-8" />
          Aucune date d'échéance renseignée. Ajoutez une date de renouvellement sur vos contrats dans « Mes Contrats ».
        </div>
      ) : (
        <>
          {actionable > 0 && (
            <div data-testid="chatel-alert" className="rounded-2xl border border-destructive/40 bg-destructive/5 p-5 flex items-start gap-3">
              <ShieldAlert className="w-5 h-5 shrink-0 text-destructive mt-0.5" />
              <div>
                <p className="font-heading font-bold text-destructive">
                  {actionable} contrat{actionable > 1 ? "s" : ""} à surveiller — résiliation loi Chatel
                </p>
                <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                  Certaines échéances approchent. Anticipez le préavis (souvent 2 mois avant la date) pour éviter la reconduction tacite. Générez votre lettre recommandée en un clic ci-dessous.
                </p>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {items.map((it) => {
              const u = urgency(it.days_left);
              return (
                <div key={it.id} data-testid={`reminder-${it.id}`} className={`rounded-2xl border p-5 ${u.bg}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary grid place-items-center shrink-0">
                      <CalendarClock className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-heading font-bold">{it.name}</p>
                      <p className="text-sm text-muted-foreground">{it.insurer} · échéance le {fmt(it.renewal_date)}</p>
                    </div>
                    <span className={`text-sm font-semibold font-mono ${u.cls}`}>{u.label}</span>
                    <button
                      data-testid={`gen-letter-${it.id}`}
                      onClick={() => genLetter(it.id, it.name)}
                      className="flex items-center gap-2 h-10 px-4 rounded-xl border border-border text-sm font-medium hover:bg-muted transition"
                    >
                      <FileSignature className="w-4 h-4" /> Lettre de résiliation
                    </button>
                  </div>
                  {u.act && (
                    <div className="mt-3 pt-3 border-t border-border/60 flex items-start gap-2 text-xs text-muted-foreground">
                      <Scale className="w-3.5 h-3.5 shrink-0 mt-0.5 text-ochre" />
                      <span>{chatelNote(it.days_left)}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <LetterModal
        open={letter.open}
        loading={letter.loading}
        contractName={letter.name}
        letter={letter.text}
        onClose={() => setLetter({ open: false, loading: false, name: "", text: "" })}
      />
    </div>
  );
}
